import { faCircleXmark, faClock, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Link } from 'react-router-dom';

import { Button, VendorLogo } from '@/ui';

import './styles.css';
import type { Props } from './types';

const formatStartedAt = (startedAt: string) =>
  new Date(startedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const AutomatedInsuranceRunStatus = ({
  state,
  vendor,
  serviceCode,
  startedAt,
  startedBy,
  errorText,
  retryNote,
  timelineUrl,
  onRetry,
  onRunWithOptions,
  onEnterManually,
  onRefresh,
  className
}: Props) => {
  if (state === 'succeeded') return null;

  if (state === 'running') {
    const details = [
      startedAt && `started ${formatStartedAt(startedAt)}${startedBy ? ` by ${startedBy}` : ''}`,
      serviceCode && `service code ${serviceCode}`,
      'usually 1–2 minutes'
    ].filter(Boolean);

    return (
      <div
        role="status"
        className={`flex items-center gap-2.5 rounded-md bg-blue-50 px-3 py-2.5 text-sm text-blue-900 ${className ?? ''}`}>
        <FontAwesomeIcon icon={faRobot} className="h-4 w-4 shrink-0 text-blue-700" />
        <span className="inline-flex flex-wrap items-center gap-1.5">
          Albert is checking
          <VendorLogo source={vendor} size="sm" showName />
          {`· ${details.join(' · ')}`}
        </span>
      </div>
    );
  }

  if (state === 'slow') {
    return (
      <div
        role="status"
        className={`flex items-start gap-2.5 rounded-md bg-yellow-100 px-3 py-2.5 text-sm text-yellow-900 ${className ?? ''}`}>
        <FontAwesomeIcon icon={faClock} className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="flex flex-col gap-2.5">
          <span>
            <strong>Still running after 10 minutes.</strong> Albert usually finishes within 5.
          </span>
          <span className="flex flex-wrap items-center gap-3">
            {onRefresh && (
              <Button variant="outline" color="neutral" size="sm" onClick={onRefresh}>
                Refresh
              </Button>
            )}
            {timelineUrl && (
              <Link to={timelineUrl} className="font-medium underline">
                Check Timeline ↗
              </Link>
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className={`flex items-start gap-2.5 rounded-md bg-uniform-red-100 px-3 py-2.5 text-sm text-uniform-red-900 ${className ?? ''}`}>
      <FontAwesomeIcon icon={faCircleXmark} className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex grow flex-col gap-2.5">
        <span>
          <strong>Albert couldn&apos;t finish.</strong>
          {errorText && ` ${errorText}`}
        </span>
        {state === 'retryScheduled' && (
          <span>{retryNote ?? 'We will automatically retry this check.'}</span>
        )}
        <span className="flex flex-wrap items-center gap-2">
          {onRetry && (
            <Button variant="outline" color="neutral" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
          {onRunWithOptions && (
            <Button variant="outline" color="neutral" size="sm" onClick={onRunWithOptions}>
              Run with options…
            </Button>
          )}
          {onEnterManually && (
            <Button variant="ghost" color="neutral" size="sm" onClick={onEnterManually}>
              Enter manually
            </Button>
          )}
          {timelineUrl && (
            <Link to={timelineUrl} className="font-medium underline">
              Payer response in Timeline ↗
            </Link>
          )}
        </span>
      </div>
    </div>
  );
};

export default React.memo(AutomatedInsuranceRunStatus);
